import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Button, Heading, Link, Paragraph, Tag } from '@digdir/designsystemet-react';
import type { TeamSurveySummary } from '../../../api/workEnvironmentTypes';
import { surveyPath } from '../../WorkEnvironmentSurveyDetail/surveyRoutes';
import {
  actionOf,
  factsOf,
  OUTCOME_COLORS,
  periodText,
  reportLinks,
  segments,
  stepText,
} from '../surveyOverviewView';
import classes from '../WorkEnvironmentDashboardPage.module.css';

type Props = {
  survey: TeamSurveySummary;
  primaryId: number | null;
  onDelete: (surveyId: number) => Promise<void>;
};

const K = 'workEnvironmentDashboard';

/** One survey: where it stands in the nine steps, what it found, its reports and what to do next */
export const SurveyCard = ({ survey, primaryId, onDelete }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [failed, setFailed] = useState(false);
  const headingId = `survey-${survey.id}`;
  const step = stepText(survey, t);
  const action = actionOf(survey, primaryId, t);
  const links = reportLinks(survey, t);
  const facts = factsOf(survey, t, i18n.language);

  const remove = () =>
    onDelete(survey.id).catch(() => {
      setFailed(true);
      setConfirming(false);
    });

  return (
    <article
      aria-labelledby={headingId}
      className={classes.card}
      data-current={survey.id === primaryId || undefined}
    >
      <div className={classes.cardTop}>
        <Tag data-size="sm" data-color={OUTCOME_COLORS[survey.outcome]}>
          {t(`${K}.outcome.${survey.outcome}`)}
        </Tag>
        {survey.round !== null && <span className={classes.round}>{t(`${K}.round`, { round: survey.round })}</span>}
      </div>
      <div className={classes.titleBlock}>
        <Heading level={3} data-size="xs" id={headingId}>
          {survey.title}
        </Heading>
        <Paragraph data-size="sm" className={classes.subtle}>
          {periodText(survey, t, i18n.language)}
        </Paragraph>
      </div>
      <div className={classes.progress}>
        <ol aria-hidden="true" className={classes.segments}>
          {segments(survey).map((segment, index) => (
            <li key={index} className={classes.segment} data-state={segment} />
          ))}
        </ol>
        <Paragraph data-size="xs">
          <span className={classes.stepLabel}>{step.label}</span>
          <span className={classes.subtle}> · {step.name}</span>
        </Paragraph>
      </div>
      {facts.length > 0 && (
        <dl className={classes.facts}>
          {facts.map((fact) => (
            <div key={fact.label} className={classes.fact}>
              <dt className={classes.subtle}>{fact.label}</dt>
              <dd data-high-risk={fact.highRisk || undefined}>{fact.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className={classes.cardFooter}>
        {links.length > 0 && (
          <span className={classes.links}>
            {links.map((link) => (
              <Link key={link.to} asChild data-size="sm">
                <RouterLink to={link.to}>
                  {link.label}
                  <span className="ds-sr-only"> {t(`${K}.links.for`, { title: survey.title })}</span>
                </RouterLink>
              </Link>
            ))}
          </span>
        )}
        {survey.outcome === 'draft' && (
          <Paragraph data-size="xs" className={classes.subtle}>
            {t(`${K}.draftNote`)}
          </Paragraph>
        )}
        <span className={classes.actions}>
          {survey.canDelete && !confirming && (
            <Button
              variant="secondary"
              data-color="danger"
              data-size="sm"
              aria-label={t(`${K}.actions.deleteLabel`, { title: survey.title })}
              onClick={() => setConfirming(true)}
            >
              {t(`${K}.actions.delete`)}
            </Button>
          )}
          {action && (
            <Button asChild variant={action.variant} data-size="sm">
              <RouterLink to={surveyPath('team', survey.id)}>{action.label}</RouterLink>
            </Button>
          )}
        </span>
      </div>
      {confirming && (
        <Alert data-color="warning" role="alert" className={classes.confirm}>
          <Paragraph data-size="sm">{t(`${K}.confirmDelete.text`, { title: survey.title })}</Paragraph>
          <span className={classes.actions}>
            <Button variant="primary" data-color="danger" data-size="sm" onClick={remove}>
              {t(`${K}.confirmDelete.confirm`)}
            </Button>
            <Button variant="tertiary" data-size="sm" onClick={() => setConfirming(false)}>
              {t(`${K}.confirmDelete.cancel`)}
            </Button>
          </span>
        </Alert>
      )}
      {failed && (
        <Alert data-color="danger" data-size="sm">
          {t(`${K}.confirmDelete.failed`)}
        </Alert>
      )}
    </article>
  );
};
